import { createContext, useContext, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth'
import { auth, googleProvider } from '../config/firebase.js'
import userService from '../services/userService.js'

const AuthContext = createContext(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [backendUser, setBackendUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [backendLoading, setBackendLoading] = useState(true)

  /**
   * Sync Firebase user with backend — creates/updates user record and stores token
   */
  const syncWithBackend = async (firebaseUser) => {
    try {
      const token = await firebaseUser.getIdToken()
      // Store token so api.js interceptor can attach it
      localStorage.setItem('authToken', token)

      // Verify token with backend (creates user if first login)
      const profile = await userService.verifyToken(token)

      // If the backend profile has no avatar but Firebase does, patch it now
      if (!profile.avatar && firebaseUser.photoURL) {
        try {
          const updated = await userService.updateProfile({ avatar: firebaseUser.photoURL })
          setBackendUser({ ...profile, avatar: firebaseUser.photoURL, ...updated })
        } catch {
          // Non-critical — still set profile with Firebase photo as local fallback
          setBackendUser({ ...profile, avatar: firebaseUser.photoURL })
        }
      } else {
        setBackendUser(profile)
      }
      return profile
    } catch (err) {
      console.warn('[AuthContext] Backend sync failed (backend may be offline):', err.message)
      // Still allow frontend to work — backend is optional for auth
      setBackendUser(null)
    }
  }

  // Listen to Firebase auth state changes
  useEffect(() => {
    // Test bypass: if window.__TEST_USER is set (injected by Playwright), use it directly
    if (typeof window !== 'undefined' && window.__TEST_USER) {
      setUser(window.__TEST_USER)
      setBackendUser(window.__TEST_USER)
      setLoading(false)
      return
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser)
      if (firebaseUser) {
        setBackendLoading(true)
        await syncWithBackend(firebaseUser)
        setBackendLoading(false)
      } else {
        localStorage.removeItem('authToken')
        setBackendUser(null)
        setBackendLoading(false)
      }
      setLoading(false)
    })

    return unsubscribe
  }, [])

  /**
   * Email/password login
   */
  const login = async ({ email, password }) => {
    const result = await signInWithEmailAndPassword(auth, email, password)
    await syncWithBackend(result.user)
    return result.user
  }

  /**
   * Email/password signup
   */
  const signup = async ({ email, password }) => {
    const result = await createUserWithEmailAndPassword(auth, email, password)
    await syncWithBackend(result.user)
    return result.user
  }

  /**
   * Google sign-in
   */
  const loginWithGoogle = async () => {
    const result = await signInWithPopup(auth, googleProvider)
    await syncWithBackend(result.user)
    return result.user
  }

  /**
   * Logout
   */
  const logout = async () => {
    await signOut(auth)
    localStorage.removeItem('authToken')
    setUser(null)
    setBackendUser(null)
  }

  /**
   * Refresh the stored token (call before sensitive operations)
   */
  const refreshToken = async () => {
    if (user) {
      const token = await user.getIdToken(true)
      localStorage.setItem('authToken', token)
      return token
    }
    return null
  }

  const value = {
    user,
    backendUser,
    setBackendUser,
    loading,
    backendLoading,
    login,
    signup,
    loginWithGoogle,
    logout,
    refreshToken,
    profile: backendUser || (user ? {
      id: user.uid,
      name: user.displayName || '',
      email: user.email || '',
      avatar: user.photoURL || '',
    } : null),
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
